import { supabase } from './supabase';
import { Tables, Enums } from '../types/database.types';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

type Poll = Tables<'polls'>;
type PollOption = Tables<'poll_options'>;
type PollPointsConfig = Tables<'poll_points_config'>;

export interface PollBetrayalLabel {
  inherited_votes: number;
  replaced_contestant_name: string;
}

export interface PollWithOptions extends Poll {
  poll_options: (PollOption & {
    contestants?: Tables<'contestants'> | null;
    betrayal?: PollBetrayalLabel | null;
  })[];
  poll_points_config?: PollPointsConfig | null;
  seasons?: Tables<'seasons'> & {
    realities?: Tables<'realities'> | null;
  };
}

export interface VoterInfo {
  user_id: string;
  points: number;
  display_name: string;
  avatar_url: string;
  created_at: string;
  option_id?: string; // Optional, only present when viewing all voters
}

export interface PollResults {
  option_id: string;
  total_votes: number;
  total_points: number;
  voters: VoterInfo[];
}

class PollsService {
  /**
   * Get active and closed polls (for landing page)
   */
  async getActivePolls(): Promise<PollWithOptions[]> {
    const { data, error } = await supabase
      .from('polls')
      .select(`
        *,
        poll_options(
          *,
          contestants(*)
        ),
        poll_points_config(*),
        seasons(
          *,
          realities(*)
        )
      `)
      .in('status', ['active', 'closed'])
      .order('created_at', { ascending: false });

    if (error) throw error;

    const polls = (data || []) as PollWithOptions[];
    if (polls.length === 0) return [];

    const { data: betrayalLabels, error: betrayalError } = await supabase
      .rpc('get_public_poll_betrayal_labels', {
        p_poll_ids: polls.map(poll => poll.id),
      });

    if (betrayalError) throw betrayalError;

    const labelsByOption = new Map(
      (betrayalLabels || []).map(label => [label.poll_option_id, label])
    );

    return polls.map(poll => ({
      ...poll,
      poll_options: poll.poll_options.map(option => ({
        ...option,
        betrayal: labelsByOption.get(option.id) || null,
      })),
    }));
  }

  /**
   * Get poll by ID
   */
  async getPollById(pollId: string) {
    const { data, error } = await supabase
      .from('polls')
      .select(`
        *,
        poll_options(
          *,
          contestants(*)
        ),
        poll_points_config(*),
        seasons(
          *,
          realities(*)
        )
      `)
      .eq('id', pollId)
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Vote on a poll (single vote mode)
   * No longer allows changing votes - each vote is permanent and cumulative
   */
  async vote(pollId: string, optionId: string, points: number) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get poll config to check daily_reset
    const { data: config } = await supabase
      .from('poll_points_config')
      .select('daily_reset')
      .eq('poll_id', pollId)
      .maybeSingle();

    const dailyReset = config?.daily_reset || false;

    // Check if user has already voted
    if (dailyReset) {
      // Check if voted in last 2 hours
      const hasVotedToday = await this.hasUserVotedToday(pollId, user.id);
      if (hasVotedToday) {
        throw new Error('Ya has votado recientemente. Podrás votar de nuevo en 2 horas.');
      }
    } else {
      // Check if voted ever
      const hasVoted = await this.hasUserVoted(pollId, user.id);
      if (hasVoted) {
        throw new Error('Ya has votado en esta encuesta.');
      }
    }

    // Get device fingerprint for abuse detection
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    const deviceFingerprint = result.visitorId;

    // Insert new vote (no delete - votes are cumulative)
    const { data, error } = await supabase
      .from('votes')
      .insert({
        poll_id: pollId,
        option_id: optionId,
        user_id: user.id,
        points,
        device_fingerprint: deviceFingerprint,
        user_agent: navigator.userAgent,
      })
      .select()
      .single();

    if (error) {
      if (error.message?.includes('DEVICE_ACCOUNT_LIMIT')) {
        throw new Error('Este dispositivo ya tiene 3 cuentas registradas en esta encuesta.');
      }
      if (error.message?.includes('DEVICE_FINGERPRINT_REQUIRED')) {
        throw new Error('No se pudo verificar este dispositivo. Recarga la página e inténtalo de nuevo.');
      }
      if (error.message?.includes('VOTE_COOLDOWN')) {
        throw new Error('Ya has votado recientemente. Podrás votar de nuevo en 2 horas.');
      }
      throw error;
    }

    return data;
  }

  /**
   * Vote distribution (multiple votes in one poll)
   * Votes are cumulative and permanent - each vote is recorded with timestamp for history
   */
  async voteDistribution(pollId: string, distribution: { optionId: string; points: number }[]) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Calculate total points being voted
    const totalPointsToVote = distribution.reduce((sum, d) => sum + d.points, 0);

    // Validate against available points (server-side validation)
    const availablePoints = await this.getAvailablePoints(pollId, user.id);
    if (totalPointsToVote > availablePoints) {
      throw new Error(`No tienes suficientes puntos. Disponibles: ${availablePoints}, Intentando usar: ${totalPointsToVote}`);
    }

    // Validate minimum points
    if (totalPointsToVote <= 0) {
      throw new Error('Debes asignar al menos 1 punto');
    }

    // Get device fingerprint for abuse detection
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    const deviceFingerprint = result.visitorId;

    // Insert new votes (cumulative - never delete previous votes)
    const votesToInsert = distribution.map(({ optionId, points }) => ({
      poll_id: pollId,
      option_id: optionId,
      user_id: user.id,
      points,
      device_fingerprint: deviceFingerprint,
      user_agent: navigator.userAgent,
    }));

    const { data, error } = await supabase
      .from('votes')
      .insert(votesToInsert)
      .select();

    if (error) {
      if (error.message?.includes('DEVICE_ACCOUNT_LIMIT')) {
        throw new Error('Este dispositivo ya tiene 3 cuentas registradas en esta encuesta.');
      }
      if (error.message?.includes('DEVICE_FINGERPRINT_REQUIRED')) {
        throw new Error('No se pudo verificar este dispositivo. Recarga la página e inténtalo de nuevo.');
      }
      if (error.message?.includes('VOTE_COOLDOWN')) {
        throw new Error('Ya has votado recientemente. Podrás votar de nuevo en 2 horas.');
      }
      throw error;
    }

    return data;
  }

  /**
   * Get poll results - only totals (voters loaded on demand)
   */
  async getPollResults(pollId: string): Promise<PollResults[]> {
    // Get aggregated totals using RPC (super fast, no pagination needed)
    const { data: aggregated, error: aggError } = await supabase.rpc('get_poll_results_aggregated', {
      p_poll_id: pollId,
    });

    if (aggError) throw aggError;

    // Return only totals - voters will be loaded on demand
    return (aggregated || []).map((agg: any) => ({
      option_id: agg.option_id,
      total_votes: Number(agg.total_votes),
      total_points: Number(agg.total_points),
      voters: [], // Empty initially - loaded on demand
    }));
  }

  /**
   * Get voters for a specific option (loaded on demand when user clicks)
   * If optionId is undefined, returns voters from all options
   * Supports pagination with offset for infinite scroll
   */
  async getOptionVoters(pollId: string, optionId?: string, limit: number = 50, offset: number = 0): Promise<VoterInfo[]> {
    const { data: voters, error } = await supabase.rpc('get_poll_voters', {
      p_poll_id: pollId,
      p_option_id: optionId || undefined,
      p_limit: limit,
      p_offset: offset,
    });

    if (error) throw error;

    return (voters || []).map((voter: any) => ({
      user_id: voter.user_id,
      points: voter.points || 0,
      display_name: voter.display_name || 'Anonymous',
      avatar_url: voter.avatar_url || '',
      created_at: voter.created_at,
      option_id: voter.option_id, // Include option_id for "all voters" view
    }));
  }

  /**
   * Check if user has already voted (ever)
   */
  async hasUserVoted(pollId: string, userId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('votes')
      .select('id')
      .eq('poll_id', pollId)
      .eq('user_id', userId)
      .limit(1);

    if (error) throw error;
    return data && data.length > 0;
  }

  /**
   * Check if user has voted in the last 2 hours
   */
  async hasUserVotedToday(pollId: string, userId: string): Promise<boolean> {
    const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();
    const { data, error } = await supabase
      .from('votes')
      .select('id')
      .eq('poll_id', pollId)
      .eq('user_id', userId)
      .gte('created_at', twoHoursAgo)
      .limit(1);

    if (error) throw error;
    return data && data.length > 0;
  }

  /**
   * Get the timestamp of the last vote (for cooldown countdown)
   */
  async getLastVoteTime(pollId: string, userId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from('votes')
      .select('created_at')
      .eq('poll_id', pollId)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return data?.created_at || null;
  }

  /**
   * Check if user has shared today
   */
  async hasSharedToday(pollId: string, userId: string): Promise<boolean> {
    const { data, error } = await supabase.rpc('has_shared_today', {
      p_poll_id: pollId,
      p_user_id: userId,
    });

    if (error) throw error;
    return data || false;
  }

  /**
   * Get available points for a user in a poll
   */
  async getAvailablePoints(pollId: string, userId: string): Promise<number> {
    const { data, error } = await supabase.rpc('get_available_points', {
      p_poll_id: pollId,
      p_user_id: userId,
    });

    if (error) throw error;
    return data || 0;
  }

  /**
   * Share poll and earn bonus points for voting (not cumulative)
   * Bonus points are only available for voting TODAY and reset daily
   */
  async sharePoll(pollId: string, platform: Enums<'share_platform'>) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Check if user already shared today
    const today = new Date().toISOString().split('T')[0];
    const { data: existingShare } = await supabase
      .from('share_history')
      .select('id')
      .eq('poll_id', pollId)
      .eq('user_id', user.id)
      .gte('shared_at', `${today}T00:00:00`)
      .maybeSingle();

    if (existingShare) {
      throw new Error('Ya compartiste esta encuesta hoy');
    }

    // Record the share. A database trigger assigns the configured bonus so
    // clients cannot forge a larger points_earned value.
    const { data, error } = await supabase
      .from('share_history')
      .insert({
        poll_id: pollId,
        user_id: user.id,
        platform,
      })
      .select()
      .single();

    if (error) throw error;

    // NOTE: We do NOT update user's total_points here
    // These bonus points are only available for voting today
    // They are not cumulative and reset daily
    // The get_available_points function handles checking if user shared today

    return data;
  }

  /**
   * Update user points
   */
  private async updateUserPoints(
    points: number,
    reason: Enums<'points_reason'>,
    referenceId?: string
  ) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Insert points history
    await supabase.from('user_points_history').insert({
      user_id: user.id,
      points,
      reason,
      reference_id: referenceId,
      description: `${reason === 'vote' ? 'Vote' : 'Share'} points`,
    });

    // Update total points
    const { data: profile } = await supabase
      .from('profiles')
      .select('total_points')
      .eq('id', user.id)
      .single();

    const newTotal = (profile?.total_points || 0) + points;

    await supabase
      .from('profiles')
      .update({ total_points: newTotal })
      .eq('id', user.id);
  }
}

export const pollsService = new PollsService();
